import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../components/AdminLayout';
import SearchableSelect from '../components/SearchableSelect';
import { 
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  EyeIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  ChatBubbleLeftIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  PhoneIcon,
  GlobeAltIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from '../utils/toast';

const AdminOrganizations = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1
  });
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verificationData, setVerificationData] = useState({
    status: 'verified',
    notes: ''
  });
  const [statusData, setStatusData] = useState({
    status: 'active',
    reason: ''
  });
  const [exporting, setExporting] = useState(false);

  const fetchOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await api.getAllOrganizations(token, {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        status: statusFilter,
        subscription: subscriptionFilter
      });
      
      setOrganizations(response.data.organizations);
      setPagination(prev => ({
        ...prev,
        ...response.data.pagination
      }));
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, statusFilter, subscriptionFilter]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const handleVerifyOrganization = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await api.verifyOrganization(token, selectedOrg.id, verificationData);
      
      toast.success(`Organization ${verificationData.status} successfully`);
      setShowVerifyModal(false);
      setSelectedOrg(null);
      setVerificationData({ status: 'verified', notes: '' });
      fetchOrganizations();
    } catch (error) {
      console.error('Failed to verify organization:', error);
      toast.error('Failed to update organization status');
    }
  };

  const handleUpdateOrganizationStatus = async () => {
    if (submitting) return;
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/organizations/${selectedOrg.id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(statusData)
      });

      if (!response.ok) {
        throw new Error('Failed to update organization status');
      }

      const statusLabels = {
        'active': 'activated',
        'inactive': 'deactivated',
        'suspended': 'suspended',
        'pending': 'set to pending'
      };

      toast.success(`🎉 Organization "${selectedOrg.name}" has been ${statusLabels[statusData.status]}!`);
      setShowStatusModal(false);
      setSelectedOrg(null);
      setStatusData({ status: 'active', reason: '' });
      await fetchOrganizations();
      
    } catch (error) {
      console.error('Failed to update organization status:', error);
      toast.error('Failed to update organization status. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openStatusModal = (org) => {
    setSelectedOrg(org);
    setStatusData({
      status: org.status || 'active',
      reason: ''
    });
    setShowStatusModal(true);
  };

  const fetchDocuments = async (orgId) => {
    try {
      setLoadingDocuments(true);
      const token = localStorage.getItem('admin_token');
      const response = await api.getOrganizationDocuments(token, orgId);
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to load documents');
      setDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleViewDocuments = (org) => {
    setSelectedOrg(org);
    setShowDocumentsModal(true);
    fetchDocuments(org.id);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="w-3 h-3 mr-1" />
            Verified
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ClockIcon className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircleIcon className="w-3 h-3 mr-1" />
            Rejected
          </span>
        );
      case 'suspended':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircleIcon className="w-3 h-3 mr-1" />
            Suspended
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <XCircleIcon className="w-3 h-3 mr-1" />
            Unverified
          </span>
        );
    }
  };

  const getSubscriptionStatusBadge = (status, expiresAt) => {
    // Check if subscription is expired
    const now = new Date();
    const expiryDate = expiresAt ? new Date(expiresAt) : null;
    const isExpired = expiryDate && expiryDate < now;

    if (!status || status === 'inactive' || isExpired) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircleIcon className="w-3 h-3 mr-1" />
          Expired
        </span>
      );
    }

    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="w-3 h-3 mr-1" />
            Active
          </span>
        );
      case 'trial':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <ClockIcon className="w-3 h-3 mr-1" />
            Trial
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ClockIcon className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <XCircleIcon className="w-3 h-3 mr-1" />
            Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
            Unknown
          </span>
        );
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleExport = async (format = 'csv') => {
    try {
      setExporting(true);
      const token = localStorage.getItem('admin_token');
      
      const filters = {
        search: searchTerm,
        status: statusFilter,
        subscription: subscriptionFilter,
        format: format
      };

      const response = await api.exportOrganizations(token, filters);
      
      // Create blob and download
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 'application/json' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `organizations_export.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Organizations exported successfully as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export organizations data');
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Organizations</h1>
                <p className="text-gray-600">Manage and verify organizations</p>
              </div>
              
              {/* Export Buttons */}
              <div className="mt-4 sm:mt-0 flex items-center space-x-2">
                <button
                  onClick={() => handleExport('csv')}
                  disabled={exporting || organizations.length === 0}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-whatsapp-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {exporting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-whatsapp-600 mr-2"></div>
                  ) : (
                    <DocumentTextIcon className="h-4 w-4 mr-2" />
                  )}
                  Export CSV
                </button>
                <button
                  onClick={() => handleExport('json')}
                  disabled={exporting || organizations.length === 0}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-whatsapp-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {exporting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-whatsapp-600 mr-2"></div>
                  ) : (
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  )}
                  Export JSON
                </button>
              </div>
            </div>
          </div>
        </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 lg:p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search organizations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-3 py-3 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
              />
            </div>
            
            <SearchableSelect
              label=""
              name="statusFilter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { value: "", label: "All Statuses", icon: "📋" },
                { value: "verified", label: "Verified", icon: "✅" },
                { value: "pending", label: "Pending", icon: "⏳" },
                { value: "rejected", label: "Rejected", icon: "❌" },
                { value: "suspended", label: "Suspended", icon: "🚫" },
                { value: "unverified", label: "Unverified", icon: "⏸️" }
              ]}
              placeholder="Filter by status..."
              isSearchable={false}
              className="min-w-[140px]"
            />

            <SearchableSelect
              label=""
              name="subscriptionFilter"
              value={subscriptionFilter}
              onChange={(e) => setSubscriptionFilter(e.target.value)}
              options={[
                { value: "", label: "All Subscriptions", icon: "💳" },
                { value: "active", label: "Active", icon: "🟢" },
                { value: "trial", label: "Trial", icon: "🔵" },
                { value: "expired", label: "Expired", icon: "🔴" },
                { value: "none", label: "No Subscription", icon: "⚪" }
              ]}
              placeholder="Filter by subscription..."
              isSearchable={false}
              className="min-w-[160px]"
            />

            <div className="text-right">
              <p className="text-xs sm:text-sm text-gray-600">
                Total: {pagination.total} organizations
              </p>
            </div>
          </div>
        </div>

        {/* Organizations Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-whatsapp-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading organizations...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Organization
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact Info
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Owner
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subscription
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stats
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {organizations.map((org) => (
                    <tr key={org.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{org.name}</div>
                            <div className="text-sm text-gray-500">{org.business_type}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          {org.phone && (
                            <div className="flex items-center text-sm text-gray-600">
                              <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                              <a href={`tel:${org.phone}`} className="hover:text-blue-600">
                                {org.phone}
                              </a>
                            </div>
                          )}
                          {org.website && (
                            <div className="flex items-center text-sm text-gray-600">
                              <GlobeAltIcon className="h-4 w-4 text-gray-400 mr-2" />
                              <a 
                                href={org.website} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="hover:text-blue-600 truncate max-w-32"
                              >
                                {org.website.replace(/^https?:\/\//, '')}
                              </a>
                            </div>
                          )}
                          {!org.phone && !org.website && (
                            <span className="text-sm text-gray-400">No contact info</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{org.owner_name}</div>
                            <div className="text-sm text-gray-500">{org.owner_email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(org.verification_status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getSubscriptionStatusBadge(org.subscription_status, org.subscription_expires_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {org.subscription_expires_at ? (
                          <div className="text-sm">
                            <div className="text-gray-900">
                              {formatDate(org.subscription_expires_at)}
                            </div>
                            <div className={`text-xs ${
                              new Date(org.subscription_expires_at) < new Date() 
                                ? 'text-red-600' 
                                : new Date(org.subscription_expires_at) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                                  ? 'text-yellow-600'
                                  : 'text-gray-500'
                            }`}>
                              {new Date(org.subscription_expires_at) < new Date() 
                                ? 'Expired' 
                                : new Date(org.subscription_expires_at) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                                  ? 'Expires soon'
                                  : 'Active'
                              }
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">No subscription</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-4 text-sm text-gray-600">
                          <div className="flex items-center">
                            <UserIcon className="h-4 w-4 mr-1" />
                            {org.user_count} users
                          </div>
                          <div className="flex items-center">
                            <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" />
                            {org.client_count} WhatsApp agents
                          </div>
                          <div className="flex items-center">
                            <ChatBubbleLeftIcon className="h-4 w-4 mr-1" />
                            {org.message_count || 0} messages
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(org.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => window.location.href = `/admin/organizations/${org.id}/details`}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          <EyeIcon className="h-4 w-4 inline mr-1" />
                          Details
                        </button>
                        <button
                          onClick={() => handleViewDocuments(org)}
                          className="text-purple-600 hover:text-purple-900 mr-3"
                        >
                          <DocumentTextIcon className="h-4 w-4 inline mr-1" />
                          Documents
                        </button>
                        {/* <button
                          onClick={() => openStatusModal(org)}
                          className="text-orange-600 hover:text-orange-900 mr-3"
                        >
                          <ShieldExclamationIcon className="h-4 w-4 inline mr-1" />
                          Status
                        </button> */}
                        <button
                          onClick={() => {
                            setSelectedOrg(org);
                            setShowVerifyModal(true);
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span>
                    {' '}to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.limit, pagination.total)}
                    </span>
                    {' '}of{' '}
                    <span className="font-medium">{pagination.total}</span>
                    {' '}results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page === pagination.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Verify Organization Modal */}
      {showVerifyModal && selectedOrg && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Manage Organization: {selectedOrg.name}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Status
                  </label>
                  <SearchableSelect
                    label=""
                    name="verificationStatus"
                    value={verificationData.status}
                    onChange={(e) => setVerificationData(prev => ({ ...prev, status: e.target.value }))}
                    options={[
                      { value: "verified", label: "Verified", icon: "✅", description: "Organization has been verified and approved" },
                      { value: "pending", label: "Pending", icon: "⏳", description: "Organization is waiting for verification" },
                      { value: "rejected", label: "Rejected", icon: "❌", description: "Organization verification has been rejected" },
                      { value: "suspended", label: "Suspended", icon: "❌", description: "Organization has been suspended" }
                    ]}
                    placeholder="Select verification status..."
                    isSearchable={false}
                    isRequired={true}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes (optional)
                  </label>
                  <textarea
                    value={verificationData.notes}
                    onChange={(e) => setVerificationData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Add any verification notes..."
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                    rows={3}
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowVerifyModal(false);
                    setSelectedOrg(null);
                    setVerificationData({ status: 'verified', notes: '' });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleVerifyOrganization}
                  className="px-4 py-2 text-sm font-medium text-white bg-whatsapp-600 border border-transparent rounded-md hover:bg-whatsapp-700"
                >
                  Update Status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Documents Modal */}
      {showDocumentsModal && selectedOrg && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDocumentsModal(false)} />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
              <div>
                <div className="flex items-center">
                  <DocumentTextIcon className="h-8 w-8 text-purple-600 mr-3" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Verification Documents - {selectedOrg.name}
                    </h3>
                    <p className="text-sm text-gray-500">Review uploaded verification documents</p>
                  </div>
                </div>
                
                {loadingDocuments ? (
                  <div className="mt-6 text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading documents...</p>
                  </div>
                ) : (
                  <div className="mt-6">
                    {documents.length === 0 ? (
                      <div className="text-center py-8">
                        <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-4 text-lg font-medium text-gray-900">No documents uploaded</h3>
                        <p className="mt-2 text-gray-600">This organization has not uploaded any verification documents yet.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {documents.map((doc) => (
                          <div key={doc.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="text-sm font-medium text-gray-900">
                                  {doc.document_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Document'}
                                </h4>
                                <p className="text-sm text-gray-600 mt-1">{doc.file_name}</p>
                                <div className="flex items-center mt-2 space-x-4 text-xs text-gray-500">
                                  <span>Size: {(doc.file_size / 1024 / 1024).toFixed(2)} MB</span>
                                  <span>Uploaded: {new Date(doc.upload_date).toLocaleDateString()}</span>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    doc.status === 'approved' ? 'bg-green-100 text-green-800' :
                                    doc.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {doc.status || 'Pending'}
                                  </span>
                                </div>
                                {doc.rejection_reason && (
                                  <p className="text-sm text-red-600 mt-2">
                                    <strong>Rejection reason:</strong> {doc.rejection_reason}
                                  </p>
                                )}
                              </div>
                              <div className="flex-shrink-0 ml-4">
                                {doc.id && (
                                  <div className="space-y-2">
                                    <a
                                      href={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081'}/api/admin/organizations/documents/${doc.id}/view?token=${localStorage.getItem('admin_token')}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                    >
                                      <EyeIcon className="h-4 w-4 mr-2" />
                                      View
                                    </a>
                                    {(doc.file_name?.toLowerCase().endsWith('.jpg') || 
                                      doc.file_name?.toLowerCase().endsWith('.jpeg') || 
                                      doc.file_name?.toLowerCase().endsWith('.png') || 
                                      doc.file_name?.toLowerCase().endsWith('.gif')) && (
                                      <div className="mt-2">
                                        <img 
                                          src={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081'}/api/admin/organizations/documents/${doc.id}/view?token=${localStorage.getItem('admin_token')}`}
                                          alt={doc.file_name}
                                          className="h-20 w-20 object-cover rounded border"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowDocumentsModal(false);
                    setSelectedOrg(null);
                    setDocuments([]);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Organization Status Management Modal */}
      {showStatusModal && selectedOrg && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <ShieldExclamationIcon className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Manage Organization Status
                    </h3>
                    <p className="text-sm text-gray-600">{selectedOrg.name}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowStatusModal(false)}
                  disabled={submitting}
                  className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="px-6 py-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization Status
                  </label>
                  <SearchableSelect
                    label=""
                    name="organizationStatus"
                    value={statusData.status}
                    onChange={(e) => setStatusData(prev => ({ ...prev, status: e.target.value }))}
                    options={[
                      { 
                        value: "active", 
                        label: "Active", 
                        icon: "✅", 
                        description: "Organization is fully operational and can use all services"
                      },
                      { 
                        value: "inactive", 
                        label: "Inactive", 
                        icon: "⏸️", 
                        description: "Organization is temporarily deactivated but not blocked"
                      },
                      { 
                        value: "suspended", 
                        label: "Suspended", 
                        icon: "🚫", 
                        description: "Organization is blocked due to policy violations or issues"
                      },
                      { 
                        value: "pending", 
                        label: "Pending", 
                        icon: "⏳", 
                        description: "Organization is awaiting approval or review"
                      }
                    ]}
                    placeholder="Select organization status..."
                    isSearchable={false}
                    isRequired={true}
                  />
                  
                  <div className="mt-2 text-sm text-gray-600">
                    <p><strong>Current Status:</strong> <span className="font-mono">{selectedOrg.status}</span></p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Status Change <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={statusData.reason}
                    onChange={(e) => setStatusData(prev => ({ ...prev, reason: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500 resize-none"
                    placeholder="Please provide a detailed reason for this status change..."
                    required
                    disabled={submitting}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    This reason will be logged for audit purposes and may be visible to the organization.
                  </p>
                </div>

                {statusData.status === 'suspended' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-red-800">Suspension Warning</h4>
                        <p className="mt-1 text-sm text-red-700">
                          Suspending this organization will immediately block access to all services. 
                          Users will not be able to create agents, send messages, or access their dashboard.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="px-6 py-4 bg-gray-50 flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowStatusModal(false)}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateOrganizationStatus}
                disabled={submitting || !statusData.reason.trim()}
                className="px-6 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 disabled:opacity-50 flex items-center space-x-2 min-w-[120px] justify-center transition-all duration-200"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <ShieldCheckIcon className="h-4 w-4" />
                    <span>Update Status</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

export default AdminOrganizations;
